import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material'
import { subDays, subHours } from 'date-fns'
import TimePeriod from '../enums/TimePeriod'
import { RootState } from '../redux'
import { setDateRange } from '../redux/slices/query'
import DateRangeSelect, {
  DateRangeSelectToolbox,
  DateRangeVisibilityOptions
} from './DateRangeSelect'
import { humanizeDateRange } from '../utils/date'
import PageToolbar from './PageToolbar'
import { DateRange } from '@mui/icons-material'
import DateRangeAutoRefreshSwitch from './DateRangeAutoRefreshSwitch'

interface DateRangeBarProps extends DateRangeVisibilityOptions {
  showDateRange?: boolean
  showAutoRefreshSwitch?: boolean
  customControl?: ReactNode
  onAutoRefreshChange?: {
    (autoRefresh: boolean): void
  }
}

export default function DateRangeBar({
  includeOneHourOption = false,
  showAbsoluteTimePeriodOptions = true,
  showRelativeTimePeriodOptions = true,
  showDateRange = true,
  showAutoRefreshSwitch = false,
  customControl,
  onAutoRefreshChange
}: DateRangeBarProps) {
  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The date range and time period.
   */
  const { dateRange, timePeriod, autoRefresh } = useSelector((state: RootState) => state.query)

  /**
   * Indicates if the auto refresh is disabled.
   */
  const [isAutoRefreshDisabled, setIsAutoRefreshDisabled] = useState<boolean>(
    timePeriod === TimePeriod.CUSTOM
  )

  /**
   * Indicates if the bar should be fixed.
   */
  const [_isFixed, setIsFixed] = useState<boolean>(false)

  /**
   * Indicates if the screen is mobile.
   */
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  /**
   * The reference to the date range bar.
   */
  const dateRangeBar = useRef<HTMLDivElement | null>(null)

  /**
   * Handle the change of the auto-refresh switch.
   */
  function handleAutoRefreshSwitchChange(
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): void {
    if (onAutoRefreshChange) {
      onAutoRefreshChange(checked)
    }
  }

  /**
   * Get the width of the scroll bar.
   *
   * @see https://stackoverflow.com/a/13382873
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll' // forcing scrollbar to appear
    // outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps

    document.body.appendChild(outer)

    // Creating inner element and placing it in the container
    const inner = document.createElement('div')
    outer.appendChild(inner)

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    if (outer.parentNode !== null) {
      // Removing temporary elements from the DOM
      outer.parentNode.removeChild(outer)
    }

    return scrollbarWidth
  }

  /**
   * Handle scroll to the root element.
   */
  function handleScroll(): void {
    // Check if the date range bar is out of bounds.
    const isDateRangeBarOutOfBounds = (dateRangeBar?.current?.getBoundingClientRect()?.top ?? 0) < 0

    setIsFixed(isDateRangeBarOutOfBounds)
  }

  // Runs when the component mounts.
  useEffect(() => {
    const main = document.getElementById('main')
    if (main) {
      main.addEventListener('scroll', handleScroll)
      // Check if the date range bar is out of bounds.
      const isDateRangeBarOutOfBounds =
        (dateRangeBar?.current?.getBoundingClientRect()?.top ?? 0) < 0

      if (isDateRangeBarOutOfBounds) {
        setIsFixed(true)
      }

      return () => main.removeEventListener('scroll', handleScroll)
    }
  }, [])

  /**
   * Disable auto refresh when using a custom time period.
   */
  useEffect(() => {
    if (
      timePeriod === TimePeriod.CUSTOM ||
      timePeriod === TimePeriod.DAY ||
      timePeriod === TimePeriod.WEEK ||
      timePeriod === TimePeriod.MONTH
    ) {
      if (onAutoRefreshChange) {
        onAutoRefreshChange(false)
      }
      setIsAutoRefreshDisabled(true)
    } else {
      setIsAutoRefreshDisabled(false)
    }
  }, [timePeriod])

  /**
   * Update date range every minute if auto refresh is enabled.
   */
  useEffect(() => {
    if (autoRefresh) {
      const ONE_MINUTE = 60 * 1000
      const timer = setInterval(() => {
        const now = new Date()
        switch (timePeriod) {
          case TimePeriod.ONE_HOUR:
            dispatch(
              setDateRange({
                from: subHours(now, 1),
                to: now
              })
            )
            break

          case TimePeriod.TWENTYFOUR_HOURS:
            dispatch(
              setDateRange({
                from: subHours(now, 24),
                to: now
              })
            )
            break

          case TimePeriod.THREE_DAYS:
            dispatch(
              setDateRange({
                from: subDays(now, 3),
                to: now
              })
            )
            break

          case TimePeriod.SEVEN_DAYS:
            dispatch(
              setDateRange({
                from: subDays(now, 7),
                to: now
              })
            )
            break

          case TimePeriod.THIRTY_DAYS:
            dispatch(
              setDateRange({
                from: subDays(now, 30),
                to: now
              })
            )
            break

          default:
            break
        }
      }, ONE_MINUTE)

      return () => clearInterval(timer)
    }
  }, [timePeriod, autoRefresh])

  return (
    <PageToolbar
      drawer={
        <Box sx={{ width: '100%' }}>
          <DateRangeSelectToolbox
            value={dateRange}
            includeOneHourOption={includeOneHourOption}
            size="small"
            customControl={customControl}
          />
        </Box>
      }
    >
      {isMobile ? (
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          gap={3}
          pr={2}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            {/*<DateRange fontSize="small" sx={{ color: theme.palette.action.active }} />*/}
            <Typography
              fontWeight={500}
              color={theme.palette.text.primary}
              sx={{
                marginTop: '0 !important',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {humanizeDateRange(dateRange.from, dateRange.to)}
            </Typography>
          </Stack>

          {showAutoRefreshSwitch && (
            <DateRangeAutoRefreshSwitch
              disabled={isAutoRefreshDisabled}
              onChange={handleAutoRefreshSwitchChange}
            />
          )}
        </Stack>
      ) : (
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap={3}
          px={3}
          sx={{ width: '100%' }}
        >
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            alignSelf={'center'}
          >
            <DateRangeSelect
              value={dateRange}
              includeOneHourOption={includeOneHourOption}
              showRelativeTimePeriodOptions={showRelativeTimePeriodOptions}
              showAbsoluteTimePeriodOptions={showAbsoluteTimePeriodOptions}
              marginTop="0 !important"
              size="small"
              customControl={customControl}
            />
          </Stack>

          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            alignSelf={'center'}
            gap={3}
          >
            {showDateRange && (
              <Typography
                fontWeight={500}
                color={theme.palette.text.primary}
                display={{ xs: 'none', lg: 'block' }}
                sx={{
                  marginTop: '0 !important',
                  textWrap: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden'
                }}
              >
                {humanizeDateRange(dateRange.from, dateRange.to)}
              </Typography>
            )}

            {showAutoRefreshSwitch && (
              <DateRangeAutoRefreshSwitch
                disabled={isAutoRefreshDisabled}
                onChange={handleAutoRefreshSwitchChange}
              />
            )}
          </Stack>
        </Stack>
      )}
    </PageToolbar>
  )
}
